import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  ObserveIcon, CheckIcon, CloseIcon, InfoIcon, 
  InsightIcon, ArrowIcon, SparkleIcon
} from './BespokeIcons';

/**
 * ObservationsExercise - Interactive exercises from pages 95-97
 * 
 * Helps users practice distinguishing observations from evaluations
 * and translating evaluations into pure observations.
 */

// Exercise 1 Data - Observation vs Evaluation Quiz
const EXERCISE_1_QUESTIONS = [
  {
    id: 1,
    statement: "When you manipulated me yesterday...",
    isObservation: false,
    answer: "When you told me four times that you wanted me to come with you, I felt..."
  },
  {
    id: 2,
    statement: "You are not a very good listener.",
    isObservation: false,
    answer: "When you looked away while I was talking with you and pushed buttons on your phone, I felt..."
  },
  {
    id: 3,
    statement: "My dad abandoned me and now I can't even think about him without becoming so angry.",
    isObservation: false,
    answer: "When my dad moved out of the house when I was five and did not call me for six years, I felt..."
  },
  {
    id: 4,
    statement: 'When I heard her say, "John is so boring," I felt...',
    isObservation: true,
    answer: "This is a pure observation because it is quoting what someone says."
  },
  {
    id: 5,
    statement: "When my partner tries to control me, I feel like he has a lot of anger issues.",
    isObservation: false,
    answer: "When my partner told me that he does not want me to hang out with Andrew, I felt..."
  },
  {
    id: 6,
    statement: "You're depressed.",
    isObservation: false,
    answer: "When I see that you have spent more than six hours in your room each day this week, I feel..."
  },
  {
    id: 7,
    statement: "When I noticed that the garbage was still inside after I asked you to take it out, I felt...",
    isObservation: true,
    answer: "This is a pure observation."
  },
  {
    id: 8,
    statement: "When I see that you don't try as hard as you can, I feel...",
    isObservation: false,
    answer: "When I see that you have been spending less time this past week on school work, I feel..."
  },
  {
    id: 9,
    statement: "You don't seem to be having a good time.",
    isObservation: false,
    answer: "When I notice your body language, I feel..."
  },
  {
    id: 10,
    statement: "You are so beautiful.",
    isObservation: false,
    answer: "When I see you I feel so happy because I deeply value our relationship."
  },
  {
    id: 11,
    statement: "My girlfriend doesn't care about me.",
    isObservation: false,
    answer: "When my girlfriend did not call me yesterday after she told me that she would, I felt..."
  },
  {
    id: 12,
    statement: "That was so sweet of you to say that.",
    isObservation: false,
    answer: "When you told me you loved me, I felt..."
  }
];

// Exercise 1 Component - Quiz
const Exercise1Quiz = ({ onComplete }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);

  const currentQuestion = EXERCISE_1_QUESTIONS[currentIndex];
  const totalQuestions = EXERCISE_1_QUESTIONS.length;

  const handleAnswer = (isObservation) => {
    const isCorrect = isObservation === currentQuestion.isObservation;
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: { 
        userAnswer: isObservation, 
        isCorrect,
        revealed: false
      }
    }));
    
    if (isCorrect) {
      setScore(prev => prev + 1);
    }
    setShowResult(true);
  };

  const nextQuestion = () => {
    setShowResult(false);
    if (currentIndex < totalQuestions - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      onComplete?.(score + (answers[currentQuestion.id]?.isCorrect ? 0 : 0));
    }
  };

  const isAnswered = answers[currentQuestion.id] !== undefined;

  return (
    <div className="space-y-6">
      {/* Progress */}
      <div className="flex items-center justify-between text-sm text-white/60">
        <span>Question {currentIndex + 1} of {totalQuestions}</span>
        <span className="text-emerald-400">Score: {score}</span>
      </div>
      
      <div className="w-full bg-white/10 rounded-full h-1.5">
        <div 
          className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / totalQuestions) * 100}%` }}
        />
      </div>

      {/* Question Card */}
      <Card className="bg-blue-950/50 border-blue-500/30">
        <CardContent className="pt-6">
          <p className="text-white/90 text-lg leading-relaxed mb-6 italic">
            "{currentQuestion.statement}"
          </p>
          
          {!isAnswered ? (
            <div className="flex gap-3">
              <Button
                onClick={() => handleAnswer(true)}
                className="flex-1 bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30"
              >
                <CheckIcon size={16} color="#4ADE80" className="mr-2" />
                Pure Observation
              </Button>
              <Button
                onClick={() => handleAnswer(false)}
                className="flex-1 bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30"
              >
                <CloseIcon size={16} color="#FBBF24" className="mr-2" />
                Evaluation
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Result */}
              <div className={`p-4 rounded-lg border ${
                answers[currentQuestion.id].isCorrect 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : 'bg-rose-500/10 border-rose-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {answers[currentQuestion.id].isCorrect ? (
                    <>
                      <CheckIcon size={20} color="#4ADE80" />
                      <span className="text-emerald-400 font-medium">Correct!</span>
                    </>
                  ) : (
                    <>
                      <CloseIcon size={20} color="#F87171" />
                      <span className="text-rose-400 font-medium">Not quite</span>
                    </>
                  )}
                </div>
                <p className="text-white/70 text-sm">
                  This is {currentQuestion.isObservation ? 'a pure observation' : 'an evaluation'}.
                </p>
              </div>

              {/* Rewrite/Explanation */}
              <div className="p-4 bg-white/5 rounded-lg border border-white/10">
                <p className="text-xs text-blue-400 uppercase tracking-wider mb-2">
                  {currentQuestion.isObservation ? 'Why it works:' : 'As a pure observation:'}
                </p>
                <p className="text-white/80 text-sm leading-relaxed">
                  {currentQuestion.answer}
                </p>
              </div>

              {/* Next Button */}
              <Button
                onClick={nextQuestion}
                className="w-full bg-blue-500 hover:bg-blue-400 text-white"
              >
                {currentIndex < totalQuestions - 1 ? (
                  <>Next Question <ArrowIcon size={16} color="#FFFFFF" direction="right" className="ml-2" /></>
                ) : (
                  <>Complete Exercise <CheckIcon size={16} color="#FFFFFF" className="ml-2" /></>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tip */}
      <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/20 flex items-start gap-2">
        <InfoIcon size={16} color="#3B82F6" className="mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-300/80">
          <strong>Tip:</strong> Think about what a video camera would record. 
          If it contains words like "always," "never," "should," or labels a person, it's likely an evaluation.
        </p>
      </div>
    </div>
  );
};

// Exercise 2 Component - Write & Translate
const Exercise2Writing = ({ onComplete }) => {
  const [entries, setEntries] = useState([
    { evaluation: '', observation: '' },
    { evaluation: '', observation: '' },
    { evaluation: '', observation: '' }
  ]);
  const [currentEntry, setCurrentEntry] = useState(0);
  const [showGuidance, setShowGuidance] = useState(true);

  const updateEntry = (field, value) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[currentEntry][field] = value;
      return updated;
    });
  };

  const isComplete = entries.every(e => e.evaluation.trim() && e.observation.trim());

  return (
    <div className="space-y-6">
      {/* Instructions */}
      {showGuidance && (
        <Card className="bg-amber-500/10 border-amber-500/30">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <InsightIcon size={24} color="#FBBF24" className="flex-shrink-0" />
              <div>
                <h4 className="text-amber-300 font-medium mb-2">How this works</h4>
                <p className="text-white/70 text-sm leading-relaxed mb-3">
                  Think of someone close to you—a partner, parent, friend, sibling, or colleague. 
                  Write down an evaluation (positive or negative) about them, then translate it 
                  into a pure observation: what did they actually say or do?
                </p>
                <p className="text-amber-400/60 text-xs italic">
                  Example: "My son never listens to me" → "Today, I asked my son to take out the garbage three times and he didn't do it."
                </p>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowGuidance(false)}
                  className="mt-2 text-amber-400/60 hover:text-amber-400"
                >
                  Got it, hide this
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entry Tabs */}
      <div className="flex gap-2">
        {entries.map((entry, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentEntry(idx)}
            className={`flex-1 py-2 px-3 rounded-lg text-sm transition-all ${
              currentEntry === idx
                ? 'bg-blue-500/20 border border-blue-500/40 text-blue-300'
                : entry.evaluation && entry.observation
                  ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400'
                  : 'bg-white/5 border border-white/10 text-white/40 hover:border-white/20'
            }`}
          >
            {entry.evaluation && entry.observation ? (
              <CheckIcon size={14} color="#4ADE80" className="inline mr-1" />
            ) : null}
            Entry {idx + 1}
          </button>
        ))}
      </div>

      {/* Current Entry Form */}
      <div className="space-y-4">
        <div>
          <label className="text-sm text-amber-400 mb-2 block">
            Your Evaluation (judgment about them)
          </label>
          <Textarea
            value={entries[currentEntry].evaluation}
            onChange={(e) => updateEntry('evaluation', e.target.value)}
            placeholder="e.g., They're so inconsiderate..."
            className="bg-white/5 border-amber-500/30 text-white placeholder:text-white/30 min-h-[80px]"
          />
        </div>

        <div className="flex justify-center">
          <ArrowIcon size={24} color="#3B82F6" direction="down" />
        </div>

        <div>
          <label className="text-sm text-blue-400 mb-2 block">
            Pure Observation (what they said or did)
          </label>
          <Textarea
            value={entries[currentEntry].observation}
            onChange={(e) => updateEntry('observation', e.target.value)}
            placeholder="When they said/did... I felt..."
            className="bg-white/5 border-blue-500/30 text-white placeholder:text-white/30 min-h-[80px]"
          />
        </div>
      </div>

      {/* Complete Button */}
      <Button
        onClick={() => onComplete?.(entries)}
        disabled={!isComplete}
        className={`w-full ${
          isComplete 
            ? 'bg-emerald-500 hover:bg-emerald-400 text-white' 
            : 'bg-white/10 text-white/40 cursor-not-allowed'
        }`}
      >
        <SparkleIcon size={16} color={isComplete ? "#FFFFFF" : "#9CA3AF"} className="mr-2" />
        {isComplete ? 'Complete Exercise' : `Complete all 3 entries to continue`}
      </Button>
    </div>
  );
};

// Main Component - Observations Exercise Dialog
const ObservationsExercise = ({ 
  triggerClassName = '',
  onExerciseComplete 
}) => {
  const [open, setOpen] = useState(false);
  const [activeExercise, setActiveExercise] = useState(null); // null, 'quiz', 'writing'
  const [completedExercises, setCompletedExercises] = useState({
    quiz: false,
    writing: false
  });
  const [quizScore, setQuizScore] = useState(null);

  const handleQuizComplete = (score) => {
    setQuizScore(score);
    setCompletedExercises(prev => ({ ...prev, quiz: true }));
    setActiveExercise(null);
  };

  const handleWritingComplete = (entries) => {
    setCompletedExercises(prev => ({ ...prev, writing: true }));
    setActiveExercise(null);
    onExerciseComplete?.({ quiz: quizScore, entries });
  };

  const renderContent = () => {
    if (activeExercise === 'quiz') {
      return (
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            onClick={() => setActiveExercise(null)}
            className="text-white/50 hover:text-white"
          >
            <ArrowIcon size={16} color="#9CA3AF" direction="left" className="mr-2" />
            Back to exercises
          </Button>
          <Exercise1Quiz onComplete={handleQuizComplete} />
        </div>
      );
    }

    if (activeExercise === 'writing') {
      return (
        <div className="space-y-4">
          <Button 
            variant="ghost" 
            onClick={() => setActiveExercise(null)}
            className="text-white/50 hover:text-white"
          >
            <ArrowIcon size={16} color="#9CA3AF" direction="left" className="mr-2" />
            Back to exercises
          </Button>
          <Exercise2Writing onComplete={handleWritingComplete} />
        </div>
      );
    }

    // Exercise Selection Screen
    return (
      <div className="space-y-6">
        {/* Introduction */}
        <div className="text-center space-y-3">
          <h3 className="text-xl text-white font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>
            What is an Observation?
          </h3>
          <p className="text-white/70 text-sm leading-relaxed">
            An observation is what you notice, independent of any evaluation—what a camera would record.
            Separating observations from judgments helps others hear your message without defensiveness.
          </p>
        </div>

        {/* Exercise Cards */}
        <div className="grid gap-4">
          {/* Exercise 1 Card */}
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${
              completedExercises.quiz 
                ? 'bg-emerald-950/50 border-emerald-500/30' 
                : 'bg-blue-950/50 border-blue-500/30 hover:border-blue-400/50'
            }`}
            onClick={() => setActiveExercise('quiz')}
          >
            <CardContent className="pt-4">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  completedExercises.quiz ? 'bg-emerald-500/20' : 'bg-blue-500/20'
                }`}>
                  {completedExercises.quiz ? (
                    <CheckIcon size={24} color="#4ADE80" />
                  ) : (
                    <ObserveIcon size={24} color="#3B82F6" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-medium">Exercise 1: Observation Quiz</h4>
                    {completedExercises.quiz && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        {quizScore}/12
                      </Badge>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">
                    Identify whether statements are pure observations or evaluations. 
                    12 scenarios with instant feedback.
                  </p>
                </div>
                <ArrowIcon size={20} color="#6B7280" direction="right" />
              </div>
            </CardContent>
          </Card>

          {/* Exercise 2 Card */}
          <Card 
            className={`cursor-pointer transition-all hover:scale-[1.02] ${
              completedExercises.writing 
                ? 'bg-emerald-950/50 border-emerald-500/30' 
                : 'bg-amber-950/50 border-amber-500/30 hover:border-amber-400/50'
            }`}
            onClick={() => setActiveExercise('writing')}
          >
            <CardContent className="pt-4">
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  completedExercises.writing ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                }`}>
                  {completedExercises.writing ? (
                    <CheckIcon size={24} color="#4ADE80" />
                  ) : (
                    <InsightIcon size={24} color="#FBBF24" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-white font-medium">Exercise 2: Write & Translate</h4>
                    {completedExercises.writing && (
                      <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        Done
                      </Badge>
                    )}
                  </div>
                  <p className="text-white/60 text-sm">
                    Write evaluations about someone close to you, then translate them 
                    into pure observations.
                  </p>
                </div>
                <ArrowIcon size={20} color="#6B7280" direction="right" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Key Insight */}
        <div className="p-4 rounded-lg bg-white/5 border border-white/10">
          <p className="text-sm text-white/60 leading-relaxed">
            <strong className="text-amber-400">Remember:</strong> Most of us have been conditioned 
            to avoid feeling our feelings by judging others. When someone triggers us, we often 
            label them as "bad" to avoid our own pain. Pure observations break this pattern.
          </p>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`gap-1.5 text-blue-400/70 hover:text-blue-400 hover:bg-blue-500/10 ${triggerClassName}`}
          data-testid="observations-exercise-trigger"
        >
          <ObserveIcon size={16} color="#3B82F6" />
          <span className="text-xs">Observe Practice</span>
        </Button>
      </DialogTrigger>

      <DialogContent 
        className="bg-[#0f1419] border-white/10 text-white max-w-xl max-h-[85vh] overflow-y-auto" 
        data-testid="observations-exercise-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 flex items-center justify-center">
              <ObserveIcon size={16} color="#3B82F6" />
            </div>
            Observations Practice
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 text-[10px]">
              Chapter 2
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ObservationsExercise;
